import { Divider } from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';

import {
  BaseThreadFragmentFragmentGQLType,
  EventLogTypeGQLType,
  GetEventLogsQueryGQLType,
  ThreadActionGQLType,
  ValleyBankLoanAccountEntityMetaFragmentFragmentGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { PermissionsFn } from '~/core/PermissionGuard';
import { Thread } from '~/packages/comments/Thread';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { EntityName as BaseEntityName } from '~/packages/event-logs/EntityName';
import {
  EventLogFilter,
  useEventLogFilters,
} from '~/packages/event-logs/EventLogFilters';
import { basicStringSearch } from '~/packages/event-logs/helpers';
import { EventLogExportDataRow } from '~/packages/event-logs/types';

// Constants
export const EVENT_LOG_TYPES: EventLogTypeGQLType[] = [
  'valleyBank_loanAccount_thread_create',
];

// Types
export type EntityMeta = ValleyBankLoanAccountEntityMetaFragmentFragmentGQLType;
type EventLogData = GetEventLogsQueryGQLType['eventLogs']['items'][number];
type ThreadData = BaseThreadFragmentFragmentGQLType;

// Exports
export const permissions: PermissionsFn = (context) => {
  if (context.user.organization.slug !== 'valleyBank') return 'deny';
  return 'allow';
};

type EntityNameProps = Pick<EntityMeta, 'loanAccountId'>;
export const EntityName = ({ loanAccountId }: EntityNameProps) => {
  return (
    <BaseEntityName
      isOnEntityRoute={false}
      to={`/valley-bank/loan-accounts/summary?loanAccount=${loanAccountId}`}
      label={`Loan Account ${loanAccountId}`}
      tooltipLabel={`Loan Account ID: ${loanAccountId}`}
    />
  );
};

type ThreadFeedItemProps = {
  thread: ThreadData;
  onThreadAction?: (action: ThreadActionGQLType) => void;
  onMount?: (id: string) => void;
  onReady?: (id: string) => void;
};
export const ThreadFeedItem = ({
  thread,
  onThreadAction,
  onMount,
  onReady,
}: ThreadFeedItemProps) => {
  switch (thread.domain) {
    case 'valleyBank_loanAccountNumber': {
      return (
        <Thread
          key={thread.threadId}
          threadId={thread.threadId}
          onThreadAction={onThreadAction}
          onReady={() => onReady?.(thread.threadId)}
          onMount={() => onMount?.(thread.threadId)}
        >
          <Typography inline variant="detail-muted">
            Thread on <EntityName loanAccountId={thread.domainIdentifier} />
          </Typography>
          <Divider />
        </Thread>
      );
    }
    default: {
      return null;
    }
  }
};

type EventFeedItemProps = {
  log: EventLogData;
  onThreadAction?: (action: ThreadActionGQLType) => void;
  onMount?: (id: string) => void;
  onReady?: (id: string) => void;
};
export const EventFeedItem = ({
  log,
  onThreadAction,
  onMount,
  onReady,
}: EventFeedItemProps) => {
  switch (log.__typename) {
    case 'ValleyBankLoanAccountThreadCreateEvent': {
      return (
        <Thread
          key={log.eventId}
          threadId={log.current.id}
          onThreadAction={onThreadAction}
          onMount={() => onMount?.(log.current.id)}
          onReady={() => onReady?.(log.current.id)}
        >
          <Typography inline variant="detail-muted">
            Thread on <EntityName loanAccountId={log.entityId} />
          </Typography>
          <Divider />
        </Thread>
      );
    }

    default: {
      return null;
    }
  }
};

export const EntityFilter = () => {
  const selectedIds = useEventLogFilters((s) => s.valleyBankLoanAccountIds);
  const onSelect = useEventLogFilters((s) => s.setValleyBankLoanAccountIds);

  const [searchString, setSearchString] = useState<string>('');
  /*
  Valley SBL currently doesn't support the events page, but if that ever changes,
  uncomment the below, swap out the query for a real one, then add the data mapping

  const entityFiltersQuery = useGetValleyLoanAccounts({
    fetchPolicy: 'cache-and-network',
    variables: { limit: 25 },
  });
  if (entityFiltersQuery.error) {
    Sentry.captureException(entityFiltersQuery.error);
    return null;
  }
  if (!entityFiltersQuery.data?.items) return null;
  const entities: EntityMeta[] = entityFiltersQuery.data.items.map(
    (entity) => ({
      ...entity,
      __typename: 'ValleySBLLoanAccountEntityMeta',
    }),
  );
  */

  const entities: EntityMeta[] = useMemo(() => [], []);
  const options = useMemo(
    () =>
      entities
        .map((entity) => ({
          id: entity.loanAccountId,
          textLabel: `Loan Account ${entity.loanAccountId}`,
          data: entity,
        }))
        .filter((option) => basicStringSearch(searchString, option.textLabel)),
    [entities, searchString],
  );

  if (entities.length < 1) return null;

  return (
    <EventLogFilter
      onSearchInputChange={setSearchString}
      leftIcon={<Icon icon="dollar-sign" />}
      noneLabel="Any Loan Account"
      singularLabel="Loan Account"
      pluralLabel="Loan Accounts"
      selectedIds={selectedIds}
      options={options}
      onClose={onSelect}
    />
  );
};

/* eslint-disable camelcase */
export const toExportDataRow = (
  dataRow: EventLogExportDataRow,
  log: EventLogData,
): EventLogExportDataRow => {
  switch (log.__typename) {
    default: {
      return dataRow;
    }
  }
};
/* eslint-enable camelcase */
